import { DomainNode, NodeDTOTypes } from '@store/services/nodes/types';
import { IDomain } from '@store/services/projects/types';

export const SCOPE_FILTER_UNASSIGNED = '__unassigned_scope__';

export const SCOPE_UNASSIGNED_LABEL = 'Unassigned';

/**
 * Which scope field the Scope filter matches an entity on. The two strings
 * are the values the insight endpoint's `scope_type` parameter accepts.
 */
export type ScopeType = 'target' | 'operationalised';

export const DEFAULT_SCOPE_TYPE: ScopeType = 'target';

export const SCOPE_TYPES: ScopeType[] = ['target', 'operationalised'];

export const SCOPE_TYPE_LABELS: Record<ScopeType, string> = {
  target: 'Target',
  operationalised: 'Operationalised',
};

/**
 * The entity types that carry an Operationalised Scope: Technology, where it
 * is edited, and the types its value is carried up to. The back end's
 * insight filter uses the same list, so the canvas and the server-computed
 * insights hide the same entities.
 */
const OPERATIONALISED_SCOPE_NODE_TYPES: ReadonlyArray<string> = [
  NodeDTOTypes.StrategicPriority,
  NodeDTOTypes.StrategicEnabler,
  NodeDTOTypes.BusinessAttribute,
  NodeDTOTypes.SecurityAttribute,
  NodeDTOTypes.Service,
  NodeDTOTypes.Capabilities,
  NodeDTOTypes.Functions,
  NodeDTOTypes.Technologies,
];

export const findWideDomain = (domains: IDomain[]) => {
  return domains.find(({ level, num }) => level === 0 && num === 0);
};

/**
 * Whether an entity of `nodeType` has the scope field `scopeType` names.
 * Every entity has a Target Scope; only `OPERATIONALISED_SCOPE_NODE_TYPES`
 * have an Operationalised one.
 */
export const carriesScopeField = (
  scopeType: ScopeType,
  nodeType?: string,
): boolean =>
  scopeType === 'target' ||
  (nodeType !== undefined &&
    OPERATIONALISED_SCOPE_NODE_TYPES.includes(nodeType));

/**
 * The domain ids an entity's `scopeType` field holds.
 *
 * Target is the `direct` rows. Operationalised is a Technology's `direct`
 * rows — the field a Technology edits is stored as its own assignment — and
 * every other entity's `inherited` rows. A row without a relation type reads
 * as `direct`, the column default, and a row keyed by `id` rather than
 * `domain_id` is the legacy shape.
 */
export const scopeFieldDomainIds = (
  scopeType: ScopeType,
  nodeType: string | undefined,
  domainNodes: DomainNode[] = [],
): string[] => {
  const relation =
    scopeType === 'operationalised' && nodeType !== NodeDTOTypes.Technologies
      ? 'inherited'
      : 'direct';

  return domainNodes
    .filter((domainNode) => (domainNode.type ?? 'direct') === relation)
    .map(
      (domainNode) =>
        domainNode.domain_id ?? (domainNode as { id?: string }).id ?? '',
    )
    .filter(Boolean);
};

/**
 * Whether an entity passes a Scope selection under `scopeType`, Target when
 * omitted.
 *
 * An empty selection keeps everything. Otherwise an entity type without the
 * field is hidden; a selected id matches only an entity whose field holds
 * that exact id, so a Domain never stands for its Sub-domains; and
 * Unassigned matches an entity whose field holds no domain at all.
 */
export const checkScopeFilter = (
  selected: string[],
  nodeType?: string,
  domainNodes: DomainNode[] = [],
  scopeType: ScopeType = DEFAULT_SCOPE_TYPE,
): boolean => {
  if (!selected.length) return true;

  if (!carriesScopeField(scopeType, nodeType)) return false;

  const ids = scopeFieldDomainIds(scopeType, nodeType, domainNodes);

  if (!ids.length) return selected.includes(SCOPE_FILTER_UNASSIGNED);

  return ids.some((id) => selected.includes(id));
};

/**
 * The slice of an entity the scope rule reads.
 */
export type ScopedDto = { type?: string; domainNodes?: DomainNode[] };

/**
 * The Scope selection that survives a switch to `scopeType`: every id in
 * `alwaysKept` and every other id at least one of `dtos` holds in its
 * `scopeType` field. The rest could match nothing under the new type and
 * are dropped. The selection comes back by identity when nothing was
 * dropped.
 */
export const reconcileScopeSelection = (
  selected: string[],
  scopeType: ScopeType,
  dtos: ReadonlyArray<ScopedDto | undefined>,
  alwaysKept: ReadonlyArray<string>,
): string[] => {
  const available = new Set(alwaysKept);

  dtos.forEach((dto) => {
    if (!dto || !carriesScopeField(scopeType, dto.type)) return;

    scopeFieldDomainIds(scopeType, dto.type, dto.domainNodes).forEach((id) =>
      available.add(id),
    );
  });

  const kept = selected.filter((id) => available.has(id));

  return kept.length === selected.length ? selected : kept;
};

export type ScopeValue = { id: string; label: string };

export type ScopeDomain = ScopeValue & { subDomains: ScopeValue[] };

/**
 * A project's domains as the Scope panel lists them: the seeded Enterprise
 * wide root on its own, then each Domain with its Sub-domains.
 */
export type ScopeTree = {
  wide: ScopeValue | undefined;
  domains: ScopeDomain[];
};

const byNum = (a: IDomain, b: IDomain) => a.num - b.num;

const toScopeValue = ({ id, name }: IDomain): ScopeValue => ({
  id,
  label: name,
});

/**
 * The Scope tree of a project's domains: the root `findWideDomain`
 * identifies, the Domains whose parent is that root and, under each, the
 * Sub-domains whose parent is that Domain, both levels in Project-settings
 * order (`num`).
 *
 * Only the root is found by `level`: the back end sets `level` on the root
 * alone and stores every other domain at the column default of 1, so a
 * Sub-domain is told from a Domain by its `parentId`.
 */
export const buildScopeTree = (domains: IDomain[]): ScopeTree => {
  const wide = findWideDomain(domains);

  const childrenOf = (parentId: string) =>
    domains.filter((item) => item.parentId === parentId).sort(byNum);

  return {
    wide: wide ? toScopeValue(wide) : undefined,
    domains: wide
      ? childrenOf(wide.id).map((item) => ({
          ...toScopeValue(item),
          subDomains: childrenOf(item.id).map(toScopeValue),
        }))
      : [],
  };
};

/**
 * The values a Domain's side panel covers: the Domain, then its Sub-domains.
 */
export const scopeDomainValueIds = (domain: ScopeDomain): string[] => [
  domain.id,
  ...domain.subDomains.map(({ id }) => id),
];
